import { useId, useState, type FormEvent } from "react";
import { api } from "../api/client";
import { ErrorNote } from "../components/ErrorNote";
import { Sheet } from "../components/Sheet";
import { number } from "../text";
import { useCampaign, useCampaignQuery } from "./context";
import { CampaignError, useSummary } from "./parts";
import { InputField, inputDefaults, RevealButton, type InputValues } from "./play";
import type { CampaignEntry, CampaignReward } from "./types";

type Adventure = Pick<CampaignEntry, "id" | "title" | "visibility">;

/**
 * Completes an adventure: the summary, who took part (suggested from the sessions, F12), the inputs of the module's reward the
 * same for all and changeable per character, and a preview of what would be booked before anything is saved.
 */
export function CompleteDialog({ adventure, onClose }: { adventure: Adventure; onClose: () => void }) {
  const campaign = useCampaign();
  const reward = useCampaignQuery(["reward", adventure.id], (headers) =>
    api.GET("/api/campaigns/{id}/reward", { params: { path: { id: campaign.id }, query: { adventure: adventure.id } }, headers }),
  );
  const title = `${campaign.term("adventure")} abschließen`;
  return (
    <>
      {reward.data ? (
        <CompleteForm adventure={adventure} reward={reward.data} title={title} onClose={onClose} />
      ) : (
        <Sheet title={title} onClose={onClose}>
          {reward.error ? <CampaignError error={reward.error} reload /> : <p className="muted">Lade …</p>}
        </Sheet>
      )}
    </>
  );
}

function CompleteForm(props: { adventure: Adventure; reward: CampaignReward; title: string; onClose: () => void }) {
  const { adventure, reward } = props;
  const campaign = useCampaign();
  const formId = useId();
  const stored = useSummary(adventure.id).section;
  const [summary, setSummary] = useState<string | null>(null);
  const [chosen, setChosen] = useState<string[] | null>(null);
  const [common, setCommon] = useState<InputValues>(() => inputDefaults(reward.inputs));
  const [own, setOwn] = useState<Record<string, InputValues>>({});
  const [step, setStep] = useState<"edit" | "preview">("edit");
  const [error, setError] = useState<Error | null>(null);
  const [busy, setBusy] = useState(false);

  const text = summary ?? stored?.text ?? "";
  const participants = chosen ?? reward.participants.filter((p) => p.suggested).map((p) => p.partyMemberId);
  // A secret adventure completes without bookings only: the module writes its title into the reason of each booking (K5).
  const withoutBooking = adventure.visibility === "secret" && reward.reward;
  const payload: Record<string, unknown> = { id: adventure.id, summary: text };
  if (reward.reward) {
    payload.reward = common;
    payload.participants = withoutBooking
      ? []
      : participants.map((id) => (own[id] ? { partyMemberId: id, reward: own[id] } : { partyMemberId: id }));
  } else {
    payload.participants = participants.map((id) => ({ partyMemberId: id }));
  }
  const operation = { type: "campaign.adventure.complete", payload };
  const preview = useCampaignQuery(
    ["preview", JSON.stringify(operation)],
    (headers) => api.POST("/api/campaigns/{id}/operations/preview", { params: { path: { id: campaign.id } }, headers, body: operation }),
    step === "preview",
  );

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setError(null);
    if (!text.trim()) {
      setError(new Error("Zum Abschluss gehört die Zusammenfassung."));
      return;
    }
    if (step === "edit") {
      setStep("preview");
      return;
    }
    setBusy(true);
    try {
      await campaign.apply(operation);
      props.onClose();
    } catch (failure) {
      setError(failure instanceof Error ? failure : new Error(String(failure)));
      setBusy(false);
    }
  };

  const toggle = (id: string, on: boolean) => setChosen(on ? [...participants, id] : participants.filter((p) => p !== id));

  return (
    <Sheet
      title={props.title}
      onClose={props.onClose}
      wide
      footer={
        <>
          <button type="button" className="button secondary" onClick={step === "edit" ? props.onClose : () => setStep("edit")}>
            {step === "edit" ? "Abbrechen" : "Zurück"}
          </button>
          <button type="submit" form={formId} className="button primary" disabled={busy || (step === "preview" && !preview.data)}>
            {step === "edit" ? "Weiter zur Vorschau" : "Abschließen"}
          </button>
        </>
      }
    >
      <form id={formId} className="stack" noValidate onSubmit={(e) => void submit(e)} aria-label={props.title}>
        <p>
          <strong>{adventure.title}</strong>
        </p>
        {step === "edit" ? (
          <>
            <label className="field">
              <span>Zusammenfassung (für alle sichtbar, Pflicht)</span>
              <textarea rows={5} value={text} onChange={(e) => setSummary(e.target.value)} autoFocus />
            </label>
            {!reward.reward && (
              <p className="notice">
                {campaign.head.systemName.de} bucht beim Abschluss nichts. Gespeichert werden die Zusammenfassung und der Abschluss mit dem heutigen Datum:{" "}
                {campaign.head.play.date.de}.
              </p>
            )}
            {withoutBooking && (
              <div className="notice">
                <p>
                  „{adventure.title}“ ist geheim. Die Belohnung nennt den Titel im Protokoll der Charaktere, deshalb ist der Abschluss nur ohne Buchung möglich. Aufdecken
                  macht die Buchung möglich.
                </p>
                <RevealButton reveal={{ target: "entry", id: adventure.id, label: `„${adventure.title}“`, entryTitle: adventure.title }} />
              </div>
            )}
            <fieldset className="field">
              <legend className="muted small">Dabei waren</legend>
              {reward.participants.length === 0 && <p className="muted small">{campaign.term("party")}: noch niemand.</p>}
              <ul className="plain-list" aria-label="Teilnehmer">
                {reward.participants.map((p) => {
                  const included = participants.includes(p.partyMemberId);
                  const values = own[p.partyMemberId];
                  return (
                    <li key={p.partyMemberId} className="stack participant">
                      <label className="option">
                        <input type="checkbox" checked={included} onChange={(e) => toggle(p.partyMemberId, e.target.checked)} />
                        {p.name}
                        {reward.reward && (
                          <span className="muted small"> · {p.bookable && !withoutBooking ? "bekommt die Belohnung" : `keine Buchung: ${withoutBooking ? "geheim" : p.reason}`}</span>
                        )}
                        {!reward.reward && p.characterId && <span className="muted small"> · keine Buchung: {p.reason}</span>}
                      </label>
                      {reward.reward && included && p.bookable && !withoutBooking && p.inputs.length > 0 && (
                        <>
                          <label className="option small">
                            <input
                              type="checkbox"
                              checked={!!values}
                              onChange={(e) =>
                                setOwn((o) => {
                                  const next = { ...o };
                                  if (e.target.checked) next[p.partyMemberId] = { ...common };
                                  else delete next[p.partyMemberId];
                                  return next;
                                })
                              }
                            />
                            Eigene Werte für {p.name}
                          </label>
                          {values &&
                            p.inputs.map((input) => (
                              <InputField
                                key={input.key}
                                input={input}
                                label={`${input.label.de} (${p.name})`}
                                value={values[input.key]}
                                onChange={(v) => setOwn((o) => ({ ...o, [p.partyMemberId]: { ...o[p.partyMemberId], [input.key]: v } }))}
                              />
                            ))}
                        </>
                      )}
                    </li>
                  );
                })}
              </ul>
            </fieldset>
            {reward.reward && !withoutBooking && reward.inputs.length > 0 && (
              <fieldset className="field">
                <legend className="muted small">Belohnung für alle</legend>
                {reward.inputs.map((input) => (
                  <InputField key={input.key} input={input} value={common[input.key]} onChange={(v) => setCommon((c) => ({ ...c, [input.key]: v }))} />
                ))}
              </fieldset>
            )}
          </>
        ) : (
          <section aria-label="Vorschau der Buchungen" className="stack">
            <h3>Vorschau</h3>
            {preview.isLoading && <p className="muted">Lade …</p>}
            {preview.error && <ErrorNote error={preview.error} />}
            {preview.data && (
              <>
                <p>{preview.data.summary}</p>
                {preview.data.bookings.length === 0 ? (
                  <p className="muted">Es wird nichts gebucht.</p>
                ) : (
                  <ul className="plain-list" aria-label="Buchungen">
                    {preview.data.bookings.map((b) => (
                      <li key={b.partyMemberId}>
                        <strong>{b.name}</strong>:{" "}
                        {b.reason
                          ? `keine Buchung (${b.reason})`
                          : b.summaries.length > 0
                            ? b.summaries.join("; ")
                            : b.bookings.map((d) => `${number(d.amount)} ${d.currency}`).join(", ") || "nichts"}
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </section>
        )}
        <ErrorNote error={error} />
      </form>
    </Sheet>
  );
}
